import {
  apiGet,
  apiPost,
  apiPatch,
  type ApiResponse,
} from "../../services/genesisApi/httpsMethods";
import { UNAUTHORIZED_APP_USER_DETAIL } from "@/constants/auth";

export { UNAUTHORIZED_APP_USER_DETAIL };

export const USER_ROLES = ["ADMIN", "EDITOR", "VIEWER"] as const;

export type UserRole = (typeof USER_ROLES)[number];

/** Legacy roles may still be returned for existing users until updated. */
export type UserRoleRead = UserRole | "MANAGER" | "ANALYST";

/** Response from GET /users/me */
export interface CurrentUser {
  id: number;
  name: string;
  email: string;
  msal_oid: string;
  last_login_at: string | null;
  role: UserRoleRead;
  is_active: boolean;
  auth_type: "SSO" | "PASSWORD";
  must_change_password: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserRead {
  id: string;
  name: string;
  email: string;
  role: "ADMIN" | "EDITOR" | "VIEWER";
  is_active: boolean;
  auth_type: "SSO" | "PASSWORD";
  msal_oid: string | null;
  must_change_password: boolean;
  last_login_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface UserList {
  items: UserRead[];
  total: number;
}

export interface UserCreate {
  email: string;
  name: string;
  role: "ADMIN" | "EDITOR" | "VIEWER";
  auth_type: "SSO" | "PASSWORD";
  is_active?: boolean;
  password?: string | null;
}

export interface UserUpdate {
  name?: string;
  display_name?: string;
  role?: "ADMIN" | "EDITOR" | "VIEWER";
  is_active?: boolean;
  password?: string;
}

function unwrapApiData<T>(response: ApiResponse<T>, fallbackMessage: string): T {
  if (!response.success || response.data === undefined) {
    const message =
      typeof response.message === "string"
        ? response.message
        : response.message?.message;
    throw new Error(message || fallbackMessage);
  }
  return response.data;
}

export function isAppUserAccessDenied(
  status: number,
  message?: string,
): boolean {
  if (status === 404) {
    return true;
  }

  return status === 403 && message === UNAUTHORIZED_APP_USER_DETAIL;
}

export async function loadCurrentUser(): Promise<
  | { ok: true; user: UserRead }
  | { ok: false; denied: boolean; status: number }
> {
  const response = await apiGet<UserRead>("users/me");
  if (response.success && response.data) {
    return { ok: true, user: response.data };
  }

  const message =
    typeof response.message === "string"
      ? response.message
      : response.message?.message;

  return {
    ok: false,
    denied: isAppUserAccessDenied(response.status, message),
    status: response.status,
  };
}

export async function getMe(): Promise<UserRead> {
  const response = await apiGet<UserRead>("users/me");
  return unwrapApiData(response, "Failed to load current user");
}

export async function listUsers(params?: {
  skip?: number;
  limit?: number;
}): Promise<UserRead[]> {
  const queryParams: Record<string, string | number> = {};
  if (params?.skip != null) {
    queryParams.skip = params.skip;
  }
  if (params?.limit != null) {
    queryParams.limit = params.limit;
  }

  const response = await apiGet<UserList | UserRead[]>("users", queryParams);
  const data = unwrapApiData(response, "Failed to load users");

  if (Array.isArray(data)) {
    return data;
  }

  return data.items;
}

export async function createUser(data: UserCreate): Promise<UserRead> {
  const response = await apiPost<UserRead>("users", data);
  return unwrapApiData(response, "Failed to create user");
}

export async function updateUser(
  userId: string,
  data: UserUpdate,
): Promise<UserRead> {
  const response = await apiPatch<UserRead>(`users/${userId}`, data);
  return unwrapApiData(response, "Failed to update user");
}
